import { z } from 'zod';
// =============================================================================
// Admin chat-run reviews
// =============================================================================
//
// The background reviewer's verdict on each chat run of one conversation, for the
// admin conversation view. See `apps/backend/platform/src/domains/chat-review/`.
// Failure-mode, gate-question and screen-signal ids are plain strings here: their
// catalogues live in the backend's `agents/chat-reviewer/` and grow without a
// contract change.
const FlagVerdictsSchema = z.object({}).catchall(z.enum(['confirmed', 'overturned']));
const ChatRunReviewGateSchema = z.object({
    model: z.string(),
    outcome: z.enum(['passed', 'flagged']),
    firedQuestions: z.array(z.string()),
    /** Every question's score, 0 to 1. */
    scores: z.object({}).catchall(z.number()),
    inputTokens: z.number(),
    costMicroUsd: z.number().nullable(),
    durationMs: z.number(),
    stateTrim: z.enum(['none', 'prior', 'results']),
});
const ChatRunReviewScreenSignalSchema = z.object({
    id: z.string(),
    severity: z.enum(['high', 'medium', 'low', 'info']),
    /** Indexes into the transcript the reviewer read; empty for a run-level signal. */
    messageIndexes: z.array(z.number()),
    shape: z.string().nullable(),
});
const ChatRunReviewFindingSchema = z.object({
    mode: z.string(),
    severity: z.enum(['high', 'medium', 'low']),
    /** Index into the transcript the reviewer read (the run's input, then what it wrote). */
    messageIndex: z.number(),
    toolCallIndex: z.number().nullable(),
    userAsked: z.boolean().nullable(),
    /** The reviewer's own description of the fault. */
    shape: z.string(),
    /** A failure mode the reviewer proposes adding to the catalogue, for a fault none describes. */
    proposedKey: z.string().nullable(),
});
const ChatRunReviewBaseSchema = z.object({
    traceId: z.string(),
    createdAt: z.string(),
});
export const ChatRunReviewSchema = z.discriminatedUnion('outcome', [
    ChatRunReviewBaseSchema.extend({
        outcome: z.literal('completed'),
        verdict: z.enum(['clean', 'faulted']),
        /** True when the gate flagged the run and the writer reviewed it. */
        escalated: z.boolean(),
        gate: ChatRunReviewGateSchema,
        screenSignals: z.array(ChatRunReviewScreenSignalSchema),
        findings: z.array(ChatRunReviewFindingSchema),
        gateVerdicts: FlagVerdictsSchema.nullable(),
        signalVerdicts: FlagVerdictsSchema.nullable(),
        judgeModel: z.string().nullable(),
        writerCostMicroUsd: z.number().nullable(),
        writerDurationMs: z.number().nullable(),
    }),
    ChatRunReviewBaseSchema.extend({
        outcome: z.literal('failed'),
        failureStage: z.enum(['gate', 'writer']),
        failureReason: z.string(),
        /** Null when the gate itself failed. */
        gate: ChatRunReviewGateSchema.nullable(),
        screenSignals: z.array(ChatRunReviewScreenSignalSchema),
        gateVerdicts: FlagVerdictsSchema.nullable(),
        signalVerdicts: FlagVerdictsSchema.nullable(),
        judgeModel: z.string().nullable(),
        writerCostMicroUsd: z.number().nullable(),
    }),
    ChatRunReviewBaseSchema.extend({
        outcome: z.literal('skipped'),
        skipReason: z.enum(['no-run-input', 'no-own-turn']),
    }),
]);
/** One entry per reviewed run; a run with no entry was never reviewed (the reviewer was off, or the run predates it). */
export const ChatRunReviewListSchema = z.object({
    reviews: z.array(ChatRunReviewSchema),
});
